import blocklist from '../assets/word_blocklist.txt';

let blocklistWords = [];
let isBlocklistLoaded = false;
let loadingError = null;

// Simple hashing function
const hashWord = (word) => {
  let hash = 0;
  for (let i = 0; i < word.length; i++) {
    const char = word.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString(36); // Convert to base36 for shorter strings
};

// Load the blocklist when the file is imported
const loadBlocklist = async () => {
  try {
    //console.log('Loading blocklist...');
    const response = await fetch(blocklist);
    
    if (!response.ok) {
      throw new Error(`Failed to load blocklist: ${response.status} ${response.statusText}`);
    }
    
    const text = await response.text();
    
    if (!text || text.trim() === '') {
      console.warn('Blocklist is empty, using default validation');
      blocklistWords = [];
    } else {
      // Hash each word before storing
      blocklistWords = text.split(',').map(word => hashWord(word.trim().toLowerCase()));
      //console.log(`Blocklist loaded with ${blocklistWords.length} words`);
    }
    
    isBlocklistLoaded = true;
    loadingError = null;
  } catch (error) {
    console.error('Error loading blocklist:', error);
    loadingError = error.message;
    // Set loaded to true but with empty list so the app can still function
    isBlocklistLoaded = true;
    blocklistWords = [];
  }
};

// Retry mechanism if loading fails
const initBlocklist = async () => {
  await loadBlocklist();
  
  // If failed, retry after 2 seconds
  if (loadingError) {
    //console.log('Retrying blocklist load in 2 seconds...');
    setTimeout(() => {
      loadBlocklist().catch(err => {
        console.error('Retry failed:', err);
      });
    }, 2000);
  }
};

initBlocklist();

export const validateText = (text) => {
  // If blocklist isn't loaded yet, assume valid to avoid blocking the user
  if (!isBlocklistLoaded) {
    console.warn('Content validation not ready, skipping check');
    return { isValid: true };
  }

  if (!text) return { isValid: true };
  
  // First check exact words
  const words = text.toLowerCase()
    .split(/\s+/)
    .map(word => word.trim())
    .filter(word => word.length > 0);

  // Hash each word before checking
  const hashedWords = words.map(word => hashWord(word));

  // Check for exact matches
  const foundBlockedWords = blocklistWords.filter(blocked => 
    hashedWords.some(word => word === blocked)
  );

  // If no exact matches, check for common obfuscation patterns
  if (foundBlockedWords.length === 0) {
    // Remove common symbols and check for joined words
    const normalizedText = text.toLowerCase().replace(/[^a-z0-9]/g, '');
    
    // Only check longer strings that might be trying to hide words
    if (normalizedText.length > 10) {
      const foundObfuscated = blocklistWords.filter(blocked => {
        // Only check for blocked words that are at least 4 characters
        if (blocked.length < 4) return false;

        // Check if the string is significantly longer than the blocked word
        const isLongEnough = normalizedText.length > blocked.length + 5;
        
        // Hash the normalized text and check for matches
        const hashedNormalizedText = hashWord(normalizedText);
        const containsWord = hashedNormalizedText.includes(blocked);
        
        if (isLongEnough && containsWord) {
          //console.log('Found blocked word in obfuscated text');
        }
        
        return isLongEnough && containsWord;
      });
      
      if (foundObfuscated.length > 0) {
        return {
          isValid: false,
          error: 'Your message potentially contains inappropriate content'
        };
      }
    }
  }

  return {
    isValid: foundBlockedWords.length === 0,
    error: foundBlockedWords.length > 0 
      ? 'Your message potentially contains inappropriate content'
      : null
  };
};

// Add a way to check if validation is ready
export const isValidationReady = () => isBlocklistLoaded;

// Add a way to get the loading error if any
export const getValidationError = () => loadingError; 